<template>
  <v-row class="px-3">
    <v-col cols="12" class="pb-0 mt-3">
      <p class="title font-weight-bold mb-0">
        Configuración de Asistencia Automática con Zoom
      </p>
    </v-col>
    <v-col cols="12">
      <v-card class="little-border px-3 pt-6 pb-2" elevation="0">
        <v-row class="px-2">
          <v-col cols="12" class="pb-0">
            <v-switch
              v-model="configData.enabled"
              label="Usar asistencia automática con Zoom"
              :disabled="disabled"
              @change="handleConfigChange"
            ></v-switch>
          </v-col>

          <!-- Campos que aparecen cuando se activa -->
          <template v-if="configData.enabled">
            <v-col cols="12" md="6" class="pb-0">
              <v-select
                v-model="configData.zoomAccount"
                :items="zoomAccounts"
                item-text="displayName"
                item-value="_id"
                label="Cuenta Zoom *"
                filled
                rounded
                required
                :rules="zoomAccountRules"
                :disabled="disabled"
                @change="handleConfigChange"
              >
              </v-select>
            </v-col>

            <v-col cols="12" md="6" class="pb-0">
              <v-text-field
                v-model="configData.meetingUrl"
                label="URL de la reunión de Zoom *"
                placeholder="https://zoom.us/j/1234567890"
                hint="URL de la reunión existente de Zoom"
                persistent-hint
                filled
                rounded
                required
                :rules="meetingUrlRules"
                :disabled="disabled"
                @input="handleConfigChange"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="6" class="pb-0">
              <v-select
                v-model="configData.thresholdType"
                :items="thresholdTypeOptions"
                label="Tipo de umbral de asistencia *"
                filled
                rounded
                required
                :disabled="disabled || isFixedOrganization"
                @change="handleConfigChange"
              >
              </v-select>
            </v-col>

            <v-col cols="12" md="6" class="pb-0">
              <v-text-field
                v-model.number="configData.attendanceThreshold"
                type="number"
                :label="thresholdLabel"
                :placeholder="thresholdPlaceholder"
                :hint="thresholdHint"
                persistent-hint
                filled
                rounded
                required
                :rules="thresholdRules"
                :disabled="disabled || isFixedOrganization"
                @input="handleConfigChange"
              ></v-text-field>
            </v-col>

            <!-- Campo de duración total - solo para porcentaje -->
            <v-col cols="12" md="6" class="pb-0" v-if="configData.thresholdType === 'percentage'">
              <v-text-field
                v-model.number="configData.totalDurationMinutes"
                type="number"
                label="Duración total de la reunión (minutos) *"
                placeholder="Ej: 120"
                hint="Duración total de la reunión para calcular el porcentaje de asistencia"
                persistent-hint
                filled
                rounded
                required
                :rules="durationRules"
                :disabled="disabled"
                @input="handleConfigChange"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="6" class="pb-0">
              <v-checkbox
                v-model="configData.autoAttendanceEnabled"
                label="Marcar asistencia automáticamente"
                hint="Si está desactivado, solo registra el tiempo pero no marca"
                persistent-hint
                :disabled="disabled"
                @change="handleConfigChange"
              ></v-checkbox>
            </v-col>

            <!-- Información de la reunión validada -->
            <v-col cols="12" class="pb-0 mt-3" v-if="validatedMeeting">
              <v-card outlined class="pa-4">
                <v-card-title class="text-subtitle-1 pb-2 d-flex align-center">
                  <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
                  Reunión Validada
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" md="6">
                      <p class="mb-2">
                        <strong>Tema de la reunión:</strong><br />
                        {{ validatedMeeting.meetingTopic }}
                      </p>
                    </v-col>
                    <v-col cols="12" md="6">
                      <p class="mb-2">
                        <strong>ID de reunión:</strong><br />
                        {{ validatedMeeting.meetingId }}
                      </p>
                    </v-col>
                    <v-col cols="12" v-if="validatedMeeting.joinUrl">
                      <p class="mb-2">
                        <strong>Enlace de reunión:</strong><br />
                        <a :href="validatedMeeting.joinUrl" target="_blank">
                          {{ validatedMeeting.joinUrl }}
                        </a>
                      </p>
                    </v-col>
                    <v-col cols="12" v-if="validatedMeeting.settings">
                      <p class="mb-1">
                        <strong>Configuración:</strong>
                      </p>
                      <ul class="ml-4">
                        <li v-if="validatedMeeting.settings.hostEmail">
                          Host: {{ validatedMeeting.settings.hostEmail }}
                        </li>
                        <li v-if="validatedMeeting.settings.duration">
                          Duración: {{ validatedMeeting.settings.duration }} minutos
                        </li>
                      </ul>
                    </v-col>

                    <!-- Estado de registro automático de asistentes -->
                    <v-col cols="12" class="mt-1">
                      <v-alert
                        v-if="validatedMeeting.registrantsEnabled === true"
                        type="success"
                        dense
                        text
                        class="mb-0"
                      >
                        <strong>Asistencia automática activada.</strong>
                        Los miembros que entren usando WeChamber serán identificados automáticamente.
                      </v-alert>
                      <v-alert
                        v-else-if="validatedMeeting.registrantsEnabled === false"
                        type="warning"
                        dense
                        text
                        class="mb-0"
                      >
                        <strong>Registro automático no activado.</strong>
                        {{ validatedMeeting.registrantsWarning || 'No se pudo activar el registro automático.' }}
                        <div class="mt-1 caption">Para reintentar, guarda el evento nuevamente.</div>
                      </v-alert>
                      <v-alert
                        v-else
                        type="info"
                        dense
                        text
                        class="mb-0"
                      >
                        Guarda el evento para verificar si el registro automático puede activarse en esta reunión.
                      </v-alert>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </template>
        </v-row>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { mapMutations, mapState } from 'vuex';

export default {
  name: 'ZoomAttendanceConfig',
  props: {
    value: {
      type: Object,
      default: () => null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      configData: {
        enabled: false,
        zoomAccount: null,
        meetingUrl: '',
        thresholdType: 'minutes', // 'minutes' o 'percentage'
        attendanceThreshold: 30,
        totalDurationMinutes: null, // Solo para tipo percentage
        autoAttendanceEnabled: true,
      },
      zoomAccounts: [],
      thresholdTypeOptions: [
        { text: 'Minutos', value: 'minutes' },
        { text: 'Porcentaje (%)', value: 'percentage' },
      ],
      zoomAccountRules: [
        v => !!v || 'Debe seleccionar una cuenta Zoom activa',
      ],
      meetingUrlRules: [
        v => !!v || 'Debe ingresar la URL de la reunión',
        v => /^https?:\/\/.+zoom\.us\/.+/.test(v) || 'Debe ser una URL válida de Zoom',
      ],
      durationRules: [
        v => v !== null && v !== undefined && v !== '' || 'La duración es requerida',
        v => v > 0 || 'La duración debe ser mayor a 0',
      ],
    };
  },
  computed: {
    ...mapState('user', ['user']),
    
    isFixedOrganization() {
      // Organización específica que debe tener 85% fijo
      return this.user && this.user._id === '66fb2d896646ca001cb93b3f';
    },
    
    thresholdLabel() {
      if (this.configData.thresholdType === 'percentage') {
        return 'Porcentaje mínimo de permanencia *';
      }
      return 'Minutos mínimos de permanencia *';
    },
    
    thresholdPlaceholder() {
      if (this.configData.thresholdType === 'percentage') {
        return 'Ej: 85';
      }
      return 'Ej: 30';
    },
    
    thresholdHint() {
      if (this.configData.thresholdType === 'percentage') {
        return 'Los asistentes deben estar al menos este porcentaje del tiempo total para marcar asistencia';
      }
      return 'Los asistentes deben estar al menos este tiempo para marcar asistencia';
    },
    
    thresholdRules() {
      if (this.configData.thresholdType === 'percentage') {
        return [
          v => v !== null && v !== undefined && v !== '' || 'El umbral es requerido',
          v => v > 0 || 'El umbral debe ser mayor a 0',
          v => v <= 100 || 'El porcentaje no puede ser mayor a 100',
        ];
      }
      return [
        v => v !== null && v !== undefined && v !== '' || 'El umbral es requerido',
        v => v > 0 || 'El umbral debe ser mayor a 0',
      ];
    },
    
    validatedMeeting() {
      return this.value && this.value.meetingId ? this.value : null;
    },
  },
  watch: {
    value: {
      handler(newVal) {
        if (newVal) {
          this.configData = {
            enabled: true,
            zoomAccount: newVal.zoomAccount || null,
            meetingUrl: newVal.meetingUrl || '',
            thresholdType: newVal.thresholdType || 'minutes',
            attendanceThreshold: newVal.attendanceThreshold || (newVal.thresholdType === 'percentage' ? 85 : 30),
            totalDurationMinutes: newVal.totalDurationMinutes || null,
            autoAttendanceEnabled: newVal.autoAttendanceEnabled !== undefined ? newVal.autoAttendanceEnabled : true,
          };
          
          // Para retrocompatibilidad con el campo antiguo
          if (newVal.attendanceThresholdMinutes && !newVal.attendanceThreshold) {
            this.configData.attendanceThreshold = newVal.attendanceThresholdMinutes;
            this.configData.thresholdType = 'minutes';
          }
        } else {
          this.resetConfigData();
        }
      },
      deep: true,
      immediate: true,
    },
    isFixedOrganization: {
      handler(isFixed) {
        if (isFixed && this.configData.enabled) {
          // Forzar 85% para la organización específica
          this.configData.thresholdType = 'percentage';
          this.configData.attendanceThreshold = 85;
          this.handleConfigChange();
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapMutations('notification', ['show']),
    
    resetConfigData() {
      // Valores por defecto para la organización específica
      if (this.isFixedOrganization) {
        this.configData = {
          enabled: false,
          zoomAccount: null,
          meetingUrl: '',
          thresholdType: 'percentage',
          attendanceThreshold: 85,
          totalDurationMinutes: null,
          autoAttendanceEnabled: true,
        };
      } else {
        this.configData = {
          enabled: false,
          zoomAccount: null,
          meetingUrl: '',
          thresholdType: 'minutes',
          attendanceThreshold: 30,
          totalDurationMinutes: null,
          autoAttendanceEnabled: true,
        };
      }
    },
    
    handleConfigChange() {
      if (this.configData.enabled) {
        // Si es la organización específica, forzar los valores
        if (this.isFixedOrganization) {
          this.configData.thresholdType = 'percentage';
          this.configData.attendanceThreshold = 85;
        }
        
        this.$emit('input', {
          zoomAccount: this.configData.zoomAccount,
          meetingUrl: this.configData.meetingUrl,
          thresholdType: this.configData.thresholdType,
          attendanceThreshold: this.configData.attendanceThreshold,
          totalDurationMinutes: this.configData.thresholdType === 'percentage' 
            ? this.configData.totalDurationMinutes 
            : null,
          autoAttendanceEnabled: this.configData.autoAttendanceEnabled,
          // Mantener retrocompatibilidad
          attendanceThresholdMinutes: this.configData.thresholdType === 'minutes' 
            ? this.configData.attendanceThreshold 
            : null,
        });
      } else {
        this.$emit('input', null);
      }
    },

    async fetchZoomAccounts() {
      try {
        const response = await this.axios({
          method: 'GET',
          url: '/zoom-accounts',
        });

        if (response.status === 200 && response.data) {
          // Filtrar solo cuentas activas y formatear para el select
          this.zoomAccounts = response.data.zoomAccounts
            .map((account) => ({
              ...account,
              displayName: `${account.name || 'Cuenta'} (${account.zoomEmail})`,
            }));
        }
      } catch (error) {
        console.error('Error al cargar cuentas de Zoom:', error);
        this.show({
          text: 'Error al cargar las cuentas de Zoom',
          color: 'error',
        });
      }
    },
  },
  async mounted() {
    await this.fetchZoomAccounts();
  },
};
</script>

<style scoped>
.little-border {
  border: 2px solid #f0f0f0;
  border-radius: 15px;
}
</style>
